import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState, useDispatch } from '../state/AppContext';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { GO_TO_INPUT, GO_TO_ACHIEVEMENTS } from '../state/actions';
import { getCollision, triggerCollisionCompute, getCollisionStatus, getAvatarUrl } from '../api/authClient';
import Header from './Header';
import CollisionCanvas from '../galaxy/CollisionCanvas';
import UniverseSearch from './UniverseSearch';
import ArtistDetailPanel from './ArtistDetailPanel';
import CollisionPlaylistDrawer from './CollisionPlaylistDrawer';
import GalaxyPlayerController from './GalaxyPlayerController';
import ZoneSelector from './ZoneSelector';
import MilestoneModal from './MilestoneModal';
import FirstDiscoveryModal from './FirstDiscoveryModal';
import FriendInviteModal from './FriendInviteModal';
import InviteLinkModal from './InviteLinkModal';
import { useBottomBarDetect } from '../hooks/useBottomBarDetect';
import { useMilestoneTrigger } from '../hooks/useMilestoneTrigger';
import { computeCompatibilityPercent, getMatchTier } from '../utils/compatibility';
import '../styles/collision.css';
import '../styles/galaxy.css';

function computeProgressPercent(progress) {
  if (!progress) return null;
  const { phase, fetched, total } = progress;
  // Phases: similarity (0–85%), tags (85–92%), building (92–100%)
  if (phase === 'similarity' && total > 0) {
    return Math.round((fetched / total) * 85);
  }
  if (phase === 'tags') return 90;
  if (phase === 'building') return 96;
  return null;
}

function CollisionView() {
  useBottomBarDetect();
  const { collisionFriendId } = useAppState();
  const dispatch = useDispatch();
  const { user, friends, favorites, discoveredArtists, dislikes } = useAuth();
  const { refreshFriends } = useAuthActions();
  const {
    activeMilestone,
    dismissMilestone,
    firstDiscoveryActive,
    dismissFirstDiscovery,
    friendInviteActive,
    dismissFriendInvite,
  } = useMilestoneTrigger();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const canvasRef = useRef(null);

  const [collisionData, setCollisionData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, none, computing, ready, error
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [progress, setProgress] = useState(null); // { phase, fetched, total }
  const [showStatsCard, setShowStatsCard] = useState(false);

  const friend = friends.find((f) => f.id === collisionFriendId);
  const pollRef = useRef(null);

  const startPolling = useCallback((silentUpdate = false) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 150) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (silentUpdate) {
          setRecomputing(false);
        } else {
          setError('Computation timed out');
          setStatus('error');
        }
        return;
      }
      try {
        const res = await getCollisionStatus(collisionFriendId);
        const data = await res.json();
        if (data.progress) setProgress(data.progress);
        if (data.status === 'ready' && !data.isComputing) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProgress(null);
          const collRes = await getCollision(collisionFriendId);
          const collData = await collRes.json();
          if (collData.collision) {
            setCollisionData(collData.collision);
            setStatus('ready');
            if (silentUpdate) setRecomputing(false);
          }
        } else if (data.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProgress(null);
          if (silentUpdate) {
            setRecomputing(false);
          } else {
            setError('Computation failed');
            setStatus('error');
          }
        }
      } catch {
        // continue polling
      }
    }, 2000);
  }, [collisionFriendId]);

  const triggerCompute = useCallback(async () => {
    try {
      const res = await triggerCollisionCompute(collisionFriendId);
      const data = await res.json();
      if (data.status === 'ready') {
        // Already up to date, re-fetch
        const collRes = await getCollision(collisionFriendId);
        const collData = await collRes.json();
        if (collData.collision) {
          setCollisionData(collData.collision);
          setStatus('ready');
        }
      } else if (data.status === 'computing') {
        setStatus('computing');
        startPolling();
      } else if (data.error) {
        setError(data.error);
        setStatus('error');
      }
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }, [collisionFriendId, startPolling]);

  // Fetch collision data on mount
  useEffect(() => {
    if (!collisionFriendId) return;

    async function fetchCollision() {
      setStatus('loading');
      try {
        const res = await getCollision(collisionFriendId);
        const data = await res.json();

        if (data.collision && data.status === 'ready') {
          if (data.isStale) {
            // Data is stale — zones may be incorrect (e.g. a favorited artist
            // still listed as an incoming signal). Don't display the outdated
            // snapshot; show a recomputing state until fresh data arrives.
            setStatus('computing');
            startPolling();
          } else {
            setCollisionData(data.collision);
            setStatus('ready');
          }
        } else if (data.status === 'computing') {
          setStatus('computing');
          startPolling();
        } else {
          // No collision computed yet — trigger compute
          setStatus('computing');
          await triggerCompute();
        }
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    }

    fetchCollision();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [collisionFriendId, startPolling, triggerCompute]);

  // Whenever a fresh snapshot lands, sync the friends list so the persisted
  // match% on the friends tab reflects this compute without an app reload.
  useEffect(() => {
    if (collisionData?.computedAt) refreshFriends();
  }, [collisionData?.computedAt, refreshFriends]);

  const handleBack = useCallback(() => {
    dispatch({ type: GO_TO_INPUT });
  }, [dispatch]);

  const handleSelectNode = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  const handleHoverNode = useCallback(() => {}, []);

  const handleCloseCard = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const userLabel = user?.displayName || 'You';
  const friendLabel = friend?.displayName || collisionData?.friendInfo?.displayName || 'Friend';
  const mapLabel = `${userLabel} × ${friendLabel}`;

  // Playlist nodes: computed lazily when user opens the drawer,
  // so the canvas has finished building nodes from collisionData.
  const [collisionPlaylistNodes, setCollisionPlaylistNodes] = useState([]);

  // Zone selection for explore mode — default to Shared Frontier + Incoming Signals
  const [selectedZones, setSelectedZones] = useState(
    () => new Set(['shared_frontier', 'your_exploration'])
  );

  // When shared frontier and incoming signals are both empty, fall back to friend's artists
  useEffect(() => {
    if (!collisionData || !collisionData.zones) return;
    const { sharedFrontier, yourExploration } = collisionData.zones;
    const hasSharedFrontier = sharedFrontier && sharedFrontier.length > 0;
    const hasIncoming = yourExploration && yourExploration.length > 0;
    if (!hasSharedFrontier && !hasIncoming) {
      setSelectedZones(new Set(['friend_artists']));
    }
  }, [collisionData]);

  // Wrapper ref for GalaxyPlayerController that only returns nodes in selected zones
  const exploreCanvasRef = useRef(null);
  if (!exploreCanvasRef.current) {
    exploreCanvasRef.current = {};
  }
  exploreCanvasRef.current.getNodes = () => {
    const allNodes = canvasRef.current?.getNodes() || [];
    return allNodes.filter((n) => selectedZones.has(n.zone));
  };
  exploreCanvasRef.current.followNode = (node) => canvasRef.current?.followNode(node);
  exploreCanvasRef.current.resetView = () => canvasRef.current?.resetView();

  const zoneSelectorElement = useMemo(() => (
    <ZoneSelector
      selectedZones={selectedZones}
      onSelectionChange={setSelectedZones}
    />
  ), [selectedZones]);

  const handleOpenPlaylist = useCallback(() => {
    setSelectedNode(null);
    const allNodes = canvasRef.current?.getNodes() || [];
    setCollisionPlaylistNodes(
      allNodes.filter((n) => selectedZones.has(n.zone))
    );
    setShowPlaylist(true);
  }, [selectedZones]);

  const handleClosePlaylist = useCallback(() => {
    setShowPlaylist(false);
  }, []);

  const handleShare = useCallback(async () => {
    if (sharing || !collisionData) return;
    setSharing(true);
    try {
      const blob = await renderShareCard(collisionData, userLabel, friendLabel);
      const file = new File([blob], 'stellar-collision.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${userLabel} × ${friendLabel} — Stellar`,
        });
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stellar-collision.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    } finally {
      setSharing(false);
    }
  }, [sharing, collisionData, userLabel, friendLabel]);

  // Computing / loading state
  if (status !== 'ready' || !collisionData) {
    return (
      <div className="collision-view">
        <div className="collision-header-overlay">
          <Header showBack onBack={handleBack} mapName="Collide Universes" />
        </div>

        <div className="collision-status-container">
          {status === 'loading' && (
            <div className="collision-status-card">
              <div className="collision-status-spinner" />
              <p className="collision-status-text">Loading collision data...</p>
            </div>
          )}
          {status === 'computing' && (
            <div className="collision-status-card">
              <div className="collision-status-spinner" />
              <p className="collision-status-text">Computing collision between universes...</p>
              {computeProgressPercent(progress) != null ? (
                <>
                  <div className="collision-progress-bar">
                    <div
                      className="collision-progress-fill"
                      style={{ width: `${computeProgressPercent(progress)}%` }}
                    />
                  </div>
                  <p className="collision-status-hint">{computeProgressPercent(progress)}% — Analyzing artist connections</p>
                </>
              ) : (
                <p className="collision-status-hint">This may take a minute as we analyze artist connections.</p>
              )}
            </div>
          )}
          {status === 'error' && (
            <div className="collision-status-card collision-status-error">
              <p className="collision-status-text">{error || 'Something went wrong.'}</p>
              <button className="collision-retry-btn" onClick={() => triggerCompute()}>
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="collision-view">
      {/* Header */}
      <div className="collision-header-overlay">
        <Header showBack onBack={handleBack} mapName={mapLabel} />
      </div>

      {/* Collision identity badges — tap to open stats card */}
      <button
        className="collision-identity-badges"
        onClick={() => setShowStatsCard(true)}
      >
        <div className="collision-badge collision-badge-user">
          {user?.hasAvatar ? (
            <img src={getAvatarUrl(user.id)} alt="" className="collision-badge-avatar" />
          ) : (
            <span className="collision-badge-initials">{userLabel[0]}</span>
          )}
          <span className="collision-badge-name">{userLabel}</span>
        </div>
        <span className="collision-badge-stats-cta">View stats</span>
        <div className="collision-badge collision-badge-friend">
          {(friend?.hasAvatar || collisionData.friendInfo?.hasAvatar) ? (
            <img src={getAvatarUrl(collisionFriendId)} alt="" className="collision-badge-avatar" />
          ) : (
            <span className="collision-badge-initials">{friendLabel[0]}</span>
          )}
          <span className="collision-badge-name">{friendLabel}</span>
        </div>
      </button>

      {/* Canvas */}
      <CollisionCanvas
        ref={canvasRef}
        collisionData={collisionData}
        favorites={favorites}
        discoveredArtists={discoveredArtists}
        dislikes={dislikes}
        onSelectNode={handleSelectNode}
        onHoverNode={handleHoverNode}
      />

      {/* Search */}
      <UniverseSearch canvasRef={canvasRef} />

      {/* Zoom controls */}
      <div className="zoom-controls collision-zoom-override">
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(1.4)}
          title="Zoom in"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(0.7)}
          title="Zoom out"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
      </div>

      {/* Bottom toolbar */}
      <div className="galaxy-toolbar">
        <div className="toolbar-tools-wrapper">
          <button
            className="toolbar-btn toolbar-icon"
            onClick={() => canvasRef.current?.resetView()}
            title="Reset zoom"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M15 3h6v6" />
              <path d="M9 21H3v-6" />
              <path d="M21 3l-7 7" />
              <path d="M3 21l7-7" />
            </svg>
          </button>
        </div>

        <div className="toolbar-actions-group">
          <button
            className="toolbar-btn toolbar-pill"
            onClick={handleShare}
            disabled={sharing}
            title="Share"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <span>{sharing ? 'Sharing...' : 'Share'}</span>
          </button>

          <button
            className="toolbar-btn toolbar-pill"
            onClick={handleOpenPlaylist}
            title="Playlist"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            <span>Playlist</span>
          </button>

          <GalaxyPlayerController
            canvasRef={exploreCanvasRef}
            externalSelectedNode={selectedNode}
            onExternalSelectNode={handleSelectNode}
            portalSelector=".collision-view"
            hideRecsFilter
            extraControls={zoneSelectorElement}
            externalFilter={selectedZones}
          />
        </div>
      </div>

      {/* Recomputing indicator */}
      {recomputing && (
        <div className="collision-recomputing-pill">
          <div className="collision-recomputing-spinner" />
          <span>Recolliding{computeProgressPercent(progress) != null ? ` ${computeProgressPercent(progress)}%` : ''}...</span>
        </div>
      )}

      {/* Stats bar */}
      {collisionData.stats && (
        <div className="collision-stats-bar">
          <div className="collision-stat">
            <span className="collision-stat-value">{collisionData.stats.coreOverlapCount}</span>
            <span className="collision-stat-label">Shared</span>
          </div>
          <div className="collision-stat">
            <span className="collision-stat-value">{collisionData.stats.totalArtists}</span>
            <span className="collision-stat-label">Total</span>
          </div>
          <div className="collision-stat">
            <span className="collision-stat-value">{collisionData.stats.sharedFrontierCount}</span>
            <span className="collision-stat-label">Frontier</span>
          </div>
        </div>
      )}

      {/* Collision Playlist Drawer */}
      {showPlaylist && (
        <CollisionPlaylistDrawer
          onClose={handleClosePlaylist}
          collisionNodes={collisionPlaylistNodes}
          playlistName={`Stellar: ${mapLabel}`}
        />
      )}

      {/* Artist detail panel */}
      {selectedNode && !selectedNode._isZoneCenter && !showPlaylist && (
        <ArtistDetailPanel
          node={selectedNode}
          onClose={handleCloseCard}
        />
      )}

      {/* Stats card overlay */}
      {showStatsCard && (
        <CollisionStatsCard
          collisionData={collisionData}
          userLabel={userLabel}
          friendLabel={friendLabel}
          onClose={() => setShowStatsCard(false)}
          onShare={handleShare}
          sharing={sharing}
        />
      )}

      {firstDiscoveryActive && (
        <FirstDiscoveryModal onClose={dismissFirstDiscovery} />
      )}

      {!firstDiscoveryActive && friendInviteActive && (
        <FriendInviteModal
          onInvite={() => { dismissFriendInvite(); setShowInviteModal(true); }}
          onClose={dismissFriendInvite}
        />
      )}

      {!firstDiscoveryActive && !friendInviteActive && activeMilestone > 0 && (
        <MilestoneModal
          milestone={activeMilestone}
          onClose={dismissMilestone}
          onViewAchievements={() => { dismissMilestone(); dispatch({ type: GO_TO_ACHIEVEMENTS }); }}
        />
      )}

      {showInviteModal && (
        <InviteLinkModal onClose={() => setShowInviteModal(false)} />
      )}
    </div>
  );
}

function CollisionStatsCard({ collisionData, userLabel, friendLabel, onClose, onShare, sharing }) {
  const compat = compatibilityPercent(collisionData.stats);
  const sharedCount = collisionData.stats?.coreOverlapCount ?? 0;
  const tier = getMatchTier(sharedCount);
  const tags = (collisionData.topTags || []).slice(0, 3).map(
    (t) => t.charAt(0).toUpperCase() + t.slice(1)
  );
  const rarest = rarestSharedArtist(collisionData);
  const incomingRec = bestRec(collisionData.zones?.yourExploration);
  const outgoingRec = bestRec(collisionData.zones?.friendExploration);

  return (
    <div className="collision-stats-overlay" onClick={onClose}>
      <div className="collision-stats-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="collision-stats-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Title */}
        <p className="collision-stats-title">{userLabel} × {friendLabel}</p>
        <p className="collision-stats-subtitle">Universe Collision</p>

        {/* Hero compatibility */}
        <div className="collision-stats-hero">
          <span className="collision-stats-hero-value">{compat}%</span>
          <span className="collision-stats-hero-label">Compatibility</span>
        </div>

        {/* Tier badge: e.g. "Deep Match · 155 shared artists" */}
        {sharedCount > 0 && tier && (
          <div className={`collision-stats-tier collision-stats-tier-${tierSlug(tier)}`}>
            <span className="collision-stats-tier-label">{tier}</span>
            <span className="collision-stats-tier-sep">·</span>
            <span className="collision-stats-tier-count">
              {sharedCount} shared artist{sharedCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {sharedCount === 0 && (
          <p className="collision-stats-shared">No shared artists yet</p>
        )}

        {/* Genre tags */}
        {tags.length > 0 && (
          <p className="collision-stats-tags">{tags.join('  ·  ')}</p>
        )}

        {/* Rarest shared artist */}
        {rarest && (
          <div className="collision-stats-rec-card collision-stats-rec-gold">
            <span className="collision-stats-rec-label">Most Unexpected Shared Artist</span>
            <span className="collision-stats-rec-name">{rarest.name}</span>
          </div>
        )}

        {/* Recommendations */}
        {incomingRec && (
          <div className="collision-stats-rec-card collision-stats-rec-blue">
            <span className="collision-stats-rec-label">Top Rec for {truncate(userLabel, 18)}</span>
            <span className="collision-stats-rec-name">{incomingRec.name}</span>
            {(incomingRec.connectedTo?.length || 0) > 0 && (
              <span className="collision-stats-rec-connections">
                {incomingRec.connectedTo.length} connection{incomingRec.connectedTo.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {outgoingRec && (
          <div className="collision-stats-rec-card collision-stats-rec-coral">
            <span className="collision-stats-rec-label">Top Rec for {truncate(friendLabel, 18)}</span>
            <span className="collision-stats-rec-name">{outgoingRec.name}</span>
            {(outgoingRec.connectedTo?.length || 0) > 0 && (
              <span className="collision-stats-rec-connections">
                {outgoingRec.connectedTo.length} connection{outgoingRec.connectedTo.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Share button */}
        <button className="collision-stats-share-btn" onClick={onShare} disabled={sharing}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span>{sharing ? 'Sharing...' : 'Share'}</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Pick the signal artist with the most connections to the target user's artists.
 */
function bestRec(zone) {
  if (!zone || !zone.length) return null;
  let best = zone[0];
  for (const a of zone) {
    if ((a.connectedTo?.length || 0) > (best.connectedTo?.length || 0)) best = a;
  }
  return best;
}

function tierSlug(tier) {
  // "Deep Match" -> "deep", etc. — used for theme-color CSS hooks.
  return tier.split(' ')[0].toLowerCase();
}

/**
 * Compatibility percentage. Prefers the server-stored value; falls back to
 * the shared client formula for legacy snapshots that pre-date matchPercent.
 */
function compatibilityPercent(stats) {
  if (!stats) return 0;
  if (typeof stats.matchPercent === 'number') return stats.matchPercent;
  return computeCompatibilityPercent({
    shared: stats.coreOverlapCount,
    yourCount: stats.yourArtistCount,
    friendCount: stats.friendArtistCount,
  });
}

/**
 * Find the rarest (most niche) shared artist — the core overlap artist
 * with the fewest/weakest connections in the link graph.
 */
function rarestSharedArtist(collisionData) {
  const core = collisionData.zones?.coreOverlap;
  if (!core || !core.length) return null;

  const coreNames = new Set(core.map((a) => a.name));
  const strengthSum = new Map();
  for (const name of coreNames) strengthSum.set(name, 0);

  for (const link of (collisionData.links || [])) {
    if (link.type !== 'core') continue;
    if (strengthSum.has(link.source)) {
      strengthSum.set(link.source, strengthSum.get(link.source) + link.strength);
    }
    if (strengthSum.has(link.target)) {
      strengthSum.set(link.target, strengthSum.get(link.target) + link.strength);
    }
  }

  let rarest = core[0];
  let lowestStrength = Infinity;
  for (const artist of core) {
    const s = strengthSum.get(artist.name) ?? 0;
    if (s < lowestStrength) {
      lowestStrength = s;
      rarest = artist;
    }
  }
  return rarest;
}

/**
 * Render a shareable "Stellar collision" card as a PNG blob.
 * Uses a procedural nebula/starfield background styled after the Stellar aesthetic.
 */
function renderShareCard(collisionData, userLabel, friendLabel) {
  return new Promise((resolve) => {
    const W = 1080;
    const H = 1920;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d');

    drawBackground(ctx, W, H);
    drawContent(ctx, W, H, collisionData, userLabel, friendLabel);
    c.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function drawBackground(ctx, W, H) {
  // Base gradient
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#05050c');
  bg.addColorStop(0.35, '#0a0a1c');
  bg.addColorStop(0.65, '#0c0c1e');
  bg.addColorStop(1, '#06060e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Nebula glow — user color (blue-purple), top-left area
  const neb1 = ctx.createRadialGradient(W * 0.25, H * 0.35, 0, W * 0.25, H * 0.35, W * 0.6);
  neb1.addColorStop(0, 'hsla(230, 70%, 45%, 0.15)');
  neb1.addColorStop(0.4, 'hsla(260, 55%, 35%, 0.06)');
  neb1.addColorStop(1, 'transparent');
  ctx.fillStyle = neb1;
  ctx.fillRect(0, 0, W, H);

  // Nebula glow — friend color (warm coral), bottom-right area
  const neb2 = ctx.createRadialGradient(W * 0.75, H * 0.55, 0, W * 0.75, H * 0.55, W * 0.6);
  neb2.addColorStop(0, 'hsla(15, 75%, 45%, 0.12)');
  neb2.addColorStop(0.4, 'hsla(30, 60%, 35%, 0.05)');
  neb2.addColorStop(1, 'transparent');
  ctx.fillStyle = neb2;
  ctx.fillRect(0, 0, W, H);

  // Core overlap glow — gold, center
  const neb3 = ctx.createRadialGradient(W * 0.5, H * 0.42, 0, W * 0.5, H * 0.42, W * 0.35);
  neb3.addColorStop(0, 'hsla(45, 90%, 55%, 0.07)');
  neb3.addColorStop(0.5, 'hsla(45, 80%, 40%, 0.03)');
  neb3.addColorStop(1, 'transparent');
  ctx.fillStyle = neb3;
  ctx.fillRect(0, 0, W, H);

  // Starfield
  const rng = mulberry32(42); // deterministic for consistent renders
  for (let i = 0; i < 200; i++) {
    const x = rng() * W;
    const y = rng() * H;
    const r = rng() * 1.8 + 0.2;
    const a = rng() * 0.5 + 0.1;
    ctx.fillStyle = `rgba(255,255,255,${a})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // A few brighter "stars" with subtle glow
  for (let i = 0; i < 12; i++) {
    const x = rng() * W;
    const y = rng() * H;
    const r = rng() * 2 + 1.5;
    const glow = ctx.createRadialGradient(x, y, 0, x, y, r * 6);
    glow.addColorStop(0, 'rgba(255,255,255,0.25)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.fillRect(x - r * 6, y - r * 6, r * 12, r * 12);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.beginPath();
    ctx.arc(x, y, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawContent(ctx, W, H, collisionData, userLabel, friendLabel) {
  const font = "'Space Grotesk', 'SF Pro Display', system-ui, sans-serif";
  const pad = 72;

  const compat = compatibilityPercent(collisionData.stats);
  const sharedCount = collisionData.stats?.coreOverlapCount ?? 0;
  const tier = getMatchTier(sharedCount);
  const tags = (collisionData.topTags || []).slice(0, 3).map(
    (t) => t.charAt(0).toUpperCase() + t.slice(1)
  );
  const rarest = rarestSharedArtist(collisionData);
  const incomingRec = bestRec(collisionData.zones?.yourExploration);
  const outgoingRec = bestRec(collisionData.zones?.friendExploration);

  // --- Top: Stellar branding ---
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.30)';
  ctx.font = `600 28px ${font}`;
  ctx.letterSpacing = '8px';
  ctx.fillText('STELLAR', W / 2, 100);
  ctx.letterSpacing = '0px';

  // --- Title: "Name × Name" ---
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.font = `700 72px ${font}`;
  const title = `${truncate(userLabel, 12)} × ${truncate(friendLabel, 12)}`;
  ctx.fillText(title, W / 2, 200);

  // --- Subtitle ---
  ctx.fillStyle = 'rgba(255,255,255,0.40)';
  ctx.font = `500 32px ${font}`;
  ctx.fillText('Universe Collision', W / 2, 260);

  // --- Accent line ---
  const lineGrad = ctx.createLinearGradient(pad, 0, W - pad, 0);
  lineGrad.addColorStop(0, 'transparent');
  lineGrad.addColorStop(0.15, 'hsla(230, 70%, 60%, 0.5)');
  lineGrad.addColorStop(0.5, 'hsla(280, 60%, 65%, 0.7)');
  lineGrad.addColorStop(0.85, 'hsla(15, 75%, 58%, 0.5)');
  lineGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = lineGrad;
  ctx.fillRect(pad, 300, W - pad * 2, 3);

  // --- Hero stat: Compatibility % ---
  const heroY = 480;
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.font = `700 160px ${font}`;
  ctx.fillText(`${compat}%`, W / 2, heroY);

  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = `600 30px ${font}`;
  ctx.letterSpacing = '6px';
  ctx.fillText('COMPATIBILITY', W / 2, heroY + 52);
  ctx.letterSpacing = '0px';

  // --- Genre tag pills ---
  if (tags.length) {
    const tagY = heroY + 130;
    const tagStr = tags.join('  ·  ');
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = `500 30px ${font}`;
    ctx.fillText(tagStr, W / 2, tagY);
  }

  // --- Tier badge pill: tier-colored "Deep Match · 155 shared artists" ---
  const secY = heroY + 200;
  if (sharedCount > 0 && tier) {
    drawTierPill(ctx, W / 2, secY, font, tier, sharedCount);
  } else {
    ctx.fillStyle = 'rgba(255,255,255,0.50)';
    ctx.font = `600 34px ${font}`;
    ctx.fillText(`${sharedCount} shared artist${sharedCount !== 1 ? 's' : ''}`, W / 2, secY);
  }

  // --- Rarest shared artist card ---
  let curY = secY + 60;
  if (rarest) {
    const rarestH = 130;
    const rx = pad;
    const rw = W - pad * 2;

    ctx.save();
    roundRect(ctx, rx, curY, rw, rarestH, 24);
    ctx.fillStyle = 'rgba(12, 12, 30, 0.5)';
    ctx.fill();
    ctx.strokeStyle = 'hsla(45, 90%, 65%, 0.2)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    // Accent bar — gold
    ctx.save();
    roundRect(ctx, rx + 1, curY + 18, 4, rarestH - 36, 2);
    ctx.fillStyle = 'hsla(45, 90%, 65%, 0.5)';
    ctx.fill();
    ctx.restore();

    ctx.textAlign = 'left';
    ctx.fillStyle = 'hsla(45, 85%, 70%, 0.7)';
    ctx.font = `600 24px ${font}`;
    ctx.fillText('Most Unexpected Shared Artist', rx + 36, curY + 48);

    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = `700 42px ${font}`;
    ctx.fillText(truncate(rarest.name, 26), rx + 36, curY + 100);
    ctx.textAlign = 'center';

    curY += rarestH + 28;
  }

  // --- Divider ---
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.fillRect(pad + 60, curY, W - pad * 2 - 120, 1);
  curY += 40;

  // --- Recommendation cards ---
  const cardX = pad;
  const cardW = W - pad * 2;
  const cardH = 160;

  if (incomingRec) {
    drawRecCard(ctx, cardX, curY, cardW, cardH, font,
      `Top Rec for ${truncate(userLabel, 18)}`,
      incomingRec.name,
      incomingRec.connectedTo?.length || 0,
      '230, 70%, 60%');
    curY += cardH + 24;
  }

  if (outgoingRec) {
    drawRecCard(ctx, cardX, curY, cardW, cardH, font,
      `Top Rec for ${truncate(friendLabel, 18)}`,
      outgoingRec.name,
      outgoingRec.connectedTo?.length || 0,
      '15, 75%, 58%');
  }

  // --- Footer ---
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.font = `600 26px ${font}`;
  ctx.fillText('stellarmusic.io', W / 2, H - 80);
}

function drawRecCard(ctx, x, y, w, h, font, label, artistName, connectionCount, hsl) {
  // Card background
  ctx.save();
  roundRect(ctx, x, y, w, h, 24);
  ctx.fillStyle = 'rgba(12, 12, 30, 0.6)';
  ctx.fill();
  ctx.strokeStyle = `hsla(${hsl}, 0.2)`;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();

  // Accent bar on left
  ctx.save();
  roundRect(ctx, x + 1, y + 20, 4, h - 40, 2);
  ctx.fillStyle = `hsla(${hsl}, 0.6)`;
  ctx.fill();
  ctx.restore();

  // Label
  ctx.textAlign = 'left';
  ctx.fillStyle = `hsla(${hsl}, 0.75)`;
  ctx.font = `600 26px ${font}`;
  ctx.fillText(label, x + 36, y + 56);

  // Artist name
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = `700 44px ${font}`;
  ctx.fillText(truncate(artistName, 24), x + 36, y + 112);

  // Connection count badge
  if (connectionCount > 0) {
    const badge = `${connectionCount} connection${connectionCount > 1 ? 's' : ''}`;
    ctx.font = `500 22px ${font}`;
    const bw = ctx.measureText(badge).width;
    ctx.fillStyle = `hsla(${hsl}, 0.15)`;
    roundRect(ctx, x + w - bw - 52, y + 36, bw + 24, 32, 16);
    ctx.fill();
    ctx.fillStyle = `hsla(${hsl}, 0.6)`;
    ctx.fillText(badge, x + w - bw - 40, y + 58);
  }

  ctx.textAlign = 'center';
}

// Mirrors the .collision-stats-tier-* CSS scheme so the share card looks
// consistent with the in-app stats card overlay.
const TIER_COLORS = {
  Light:  { hue: 220, satL: 30, satT: 40, lT: 80, lL: 70, bg: 0.06, border: 0.30, text: 0.95 },
  Solid:  { hue: 195, satL: 65, satT: 75, lT: 72, lL: 55, bg: 0.07, border: 0.40, text: 0.97 },
  Strong: { hue: 265, satL: 70, satT: 80, lT: 78, lL: 65, bg: 0.08, border: 0.45, text: 0.97 },
  Deep:   { hue: 45,  satL: 90, satT: 90, lT: 72, lL: 65, bg: 0.10, border: 0.55, text: 0.98 },
};

function drawTierPill(ctx, cx, cy, font, tier, sharedCount) {
  const key = tier.split(' ')[0]; // "Deep Match" -> "Deep"
  const c = TIER_COLORS[key] || TIER_COLORS.Light;

  const labelFont = `700 32px ${font}`;
  const sepFont = `600 32px ${font}`;
  const countFont = `500 30px ${font}`;
  const sharedLabel = `${sharedCount} shared artist${sharedCount !== 1 ? 's' : ''}`;

  ctx.font = labelFont;
  const labelW = ctx.measureText(tier).width;
  ctx.font = sepFont;
  const sepW = ctx.measureText(' · ').width;
  ctx.font = countFont;
  const countW = ctx.measureText(sharedLabel).width;

  const innerW = labelW + sepW + countW;
  const padX = 32;
  const h = 64;
  const w = innerW + padX * 2;
  const x = cx - w / 2;
  const y = cy - h / 2 - 4;

  ctx.save();
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = `hsla(${c.hue}, ${c.satL}%, ${c.lL}%, ${c.bg})`;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = `hsla(${c.hue}, ${c.satL}%, ${c.lL}%, ${c.border})`;
  ctx.stroke();
  ctx.restore();

  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let tx = x + padX;
  const ty = y + h / 2;

  ctx.font = labelFont;
  ctx.fillStyle = `hsla(${c.hue}, ${c.satT}%, ${c.lT}%, ${c.text})`;
  ctx.fillText(tier, tx, ty);
  tx += labelW;

  ctx.font = sepFont;
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillText(' · ', tx, ty);
  tx += sepW;

  ctx.font = countFont;
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText(sharedLabel, tx, ty);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

// Deterministic PRNG for consistent starfield renders
function mulberry32(seed) {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default CollisionView;
